import React, { useState } from 'react';
import { Handle, Position } from 'reactflow';
import { Target, CheckCircle, XCircle, Clock, AlertTriangle, BarChart3, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';

const AutoEvaluatorNode = ({ data, isConnectable, selected }) => {
  const [isResultsExpanded, setIsResultsExpanded] = useState(false);
  
  // Parse execution results if available
  let executionResult = null;
  let executionStatus = null;
  
  if (data.executionResult) {
    try {
      executionResult = typeof data.executionResult === 'string' 
        ? JSON.parse(data.executionResult) 
        : data.executionResult;
      executionStatus = data.executionStatus || 'completed';
    } catch (e) {
      console.error('Error parsing execution result:', e);
    }
  }

  const getStatusIcon = () => {
    switch (executionStatus) {
      case 'completed':
        return <CheckCircle className="h-3 w-3 text-celadon-600" />;
      case 'failed':
        return <XCircle className="h-3 w-3 text-gray-500" />;
      case 'running':
        return <Clock className="h-3 w-3 text-gray-500 animate-spin" />;
      default:
        return null;
    }
  };

  const getNodeBorderColor = () => {
    if (selected) {
      return 'border-celadon-400 shadow-celadon-200/50 shadow-lg';
    }
    
    switch (executionStatus) {
      case 'completed':
        return 'border-celadon-300 shadow-celadon-100/50';
      case 'failed':
        return 'border-gray-300 shadow-gray-100/50';
      case 'running':
        return 'border-gray-300 shadow-gray-100/50';
      default:
        return 'border-gray-200 shadow-gray-50/50';
    }
  };

  const getStatusBadgeColor = () => {
    switch (executionStatus) {
      case 'completed':
        return 'bg-celadon-100 text-celadon-800';
      case 'failed':
        return 'bg-gray-100 text-gray-800';
      case 'running':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className={`
      bg-white border-2 rounded-2xl shadow-soft min-w-[240px] max-w-[320px] 
      transition-all duration-200 hover:shadow-medium
      ${getNodeBorderColor()}
    `}>
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        style={{ 
          background: '#9cc4a5',
          width: '14px',
          height: '14px',
          border: '2px solid #64926e',
          borderRadius: '50%'
        }}
        isConnectable={isConnectable}
      />
      
      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        style={{ 
          background: '#9cc4a5',
          width: '14px',
          height: '14px',
          border: '2px solid #64926e',
          borderRadius: '50%'
        }}
        isConnectable={isConnectable}
      />
      
      {/* Node Header */}
      {/* //TODO: change border color to celadon */}
      <div className="bg-gradient-to-r from-celadon-100 to-celadon-50 px-4 py-3 rounded-t-2xl border-b border-celadon-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-1.5 bg-celadon-200 rounded-lg">
              <Target className="h-4 w-4 text-celadon-800" />
            </div>
            <div>
              <span className="text-sm font-semibold text-celadon-900">
                {data.label || 'Auto-Evaluator'}
              </span>
              <div className="text-xs text-celadon-700 opacity-75">
                Quality Assessment
              </div>
            </div>
          </div>
          {executionStatus && (
            <div className="flex items-center">
              {getStatusIcon()}
            </div>
          )}
        </div>
      </div>
      
      {/* Node Content */}
      <div className="p-4 space-y-3">
        {/* Configuration Display */}
        <div className="space-y-2">
          {data.evaluation_model ? (
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-700">Model:</span>
              <span className="text-xs text-gray-600">
                {data.evaluation_model.split('-')[0]}
              </span>
            </div>
          ) : (
            <div className="text-xs text-gray-500 italic text-center py-2">
              No evaluation model configured
            </div>
          )}
          
          {data.pass_threshold && (
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-700">Threshold:</span>
              <span className="text-xs text-gray-600">
                {Math.round(data.pass_threshold * 100)}%
              </span>
            </div>
          )}
          
          {data.criteria && data.criteria.length > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-700">Criteria:</span>
              <span className="badge badge-secondary">{data.criteria.length}</span>
            </div>
          )}
        </div>

        {/* Execution Results Summary */}
        {executionResult && executionResult.output && (
          <div className="pt-3 border-t border-celadon-200">
            <button
              onClick={() => setIsResultsExpanded(!isResultsExpanded)}
              className="w-full flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <div className="flex items-center space-x-2">
                <BarChart3 className="h-3 w-3 text-gray-600" />
                <span className="text-xs font-medium text-gray-800">Evaluation Results</span>
              </div>
              {isResultsExpanded ? (
                <ChevronUp className="h-3 w-3 text-gray-500" />
              ) : (
                <ChevronDown className="h-3 w-3 text-gray-500" />
              )}
            </button>
            
            {isResultsExpanded && (
              <div className="mt-2">
                {executionResult.output.summary ? (
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <div className="font-semibold text-gray-900">{executionResult.output.summary.totalEvaluations}</div>
                      <div className="text-gray-600">Total</div>
                    </div>
                    <div className="bg-celadon-50 rounded-lg p-2 text-center">
                      <div className="font-semibold text-celadon-700">{executionResult.output.summary.passed}</div>
                      <div className="text-celadon-600">Passed</div>
                    </div>
                    <div className="bg-gray-100 rounded-lg p-2 text-center">
                      <div className="font-semibold text-gray-700">{executionResult.output.summary.failed}</div>
                      <div className="text-gray-600">Failed</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <div className="font-semibold text-gray-700">{(executionResult.output.summary.avgScore * 100).toFixed(0)}%</div>
                      <div className="text-gray-600">Avg Score</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-gray-600 text-center py-2">
                    Evaluation completed
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Error Display */}
        {executionStatus === 'failed' && executionResult?.error && (
          <div className="pt-3 border-t border-gray-200">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-2">
                <AlertTriangle className="h-3 w-3 text-gray-600" />
                <span className="text-xs font-medium text-gray-800">Error</span>
              </div>
              <div className="text-xs text-gray-700 max-h-16 overflow-hidden">
                {executionResult.error.length > 80 
                  ? `${executionResult.error.substring(0, 80)}...`
                  : executionResult.error
                }
              </div>
            </div>
          </div>
        )}

        {/* Status Bar */}
        <div className="pt-2 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {executionStatus ? (
                <span className={`
                  inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                  ${getStatusBadgeColor()}
                `}>
                  {executionStatus === 'running' && <Clock className="h-3 w-3 mr-1 animate-spin" />}
                  {executionStatus === 'completed' ? 'Complete' : executionStatus}
                </span>
              ) : (
                'Ready'
              )}
            </span>
            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoEvaluatorNode; 